/* eslint no-param-reassign: "error" */
import React, {
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useHistory,
  useLocation,
} from 'react-router-dom';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getFeatures,
} from 'web-selectors';

import {
  decodeParam,
} from 'web-utils';

import {
  Flex,
} from 'web-styled';

import {
  Icon,
  Button,
  Select,
  Tooltip,
  AddEntity,
  ContentWrapper,
  NavigationTabs,
  AssignSearchOption,
} from 'web-components';

import Header from './Header';
import Templates from './Templates';
import Documents from './Documents';
import Exercises from './Exercises';
import Videos from './Videos';
import CalendarTab from './CalendarTab';
import RightWidget from './RightWidget';
import * as S from './styled';

const resourceTypes = {
  programs: 'personalPrograms',
  workouts: 'personalWorkouts',
  exercises: 'personalExercises',
  videos: 'videos',
  calendar: 'workoutEvents',
  documents: 'documents',
  edit: 'edit',
};

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    boxShadow: 0,
    borderColor: state.isFocused
      ? '#ccc'
      : provided.borderColor,
    '&:hover': { borderColor: '#ccc' },
  }),
};

type Props = {
  id: string,
  tabName: string,
  location: any,
  clientInfo: any,
};

const UserDetails = ({
  id,
  tabName,
  clientInfo,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const params = decodeParam(location.search);
  const isArchived = params.status === 'inactive';
  const resourceType = resourceTypes[tabName];

  const [queryVideo, setQueryVideo] = useState('');
  const [newItemModal, setNewItemModal] = useState(false);

  const dispatch = useDispatch();

  const mapState = useCallback((state) => {
    const features = getFeatures(state);
    const searchResource = {
      programs: 'programTemplates',
      workouts: 'workoutTemplates',
      exercises: 'exercises',
      documents: 'documents',
    }[tabName];

    return {
      features,
      profileId: coreSelectors.getProfile('id')(state),
      folderRoots: coreSelectors.getFolderRoots(state),
      searchOptions: searchResource
        ? coreSelectors.getSearchClientOptions(
          searchResource,
          'library',
        )(state)
        : [],
    };
  }, [tabName]);

  const {
    features,
    profileId,
    folderRoots,
    searchOptions,
  } = useSelector(mapState, shallowEqual);

  const userTabs = useMemo(() => {
    const tabs = [
      'programs',
      'workouts',
      'exercises',
    ];

    if (features.video_library) tabs.push('videos');
    if (features.calendar_and_scheduling) tabs.push('calendar');
    if (features.documents) tabs.push('documents');

    return tabs.map(
      name => ({
        name: i18n.t(name),
        to: `/users/${id}/${name}`,
      }),
    );
  }, [id, features]);

  const handleSearchVideo = () => {
    dispatch(
      coreActions.requestPersonalVideos({
        q: queryVideo,
        userId: clientInfo.id,
        list: `${clientInfo.id}`,
      }),
    );
  };

  const handleSelectChange = (item) => {
    if (item?.value) {
      dispatch(
        coreActions.requestAssignClient({
          payload: {
            person_id: clientInfo.id,
            ...{
              programs: {
                program_template_id: +item.value,
              },
              workouts: {
                workout_template_id: +item.value,
              },
              exercises: {
                exercise_id: +item.value,
              },
            }[tabName],
          },
          resourceType,
          listName: {
            programs: `program_${item.value}`,
            workouts: `workout_${item.value}`,
            exercises: `exercise_${item.value}`,
          }[tabName],
        }),
      );
    } else {
      setNewItemModal(true);
    }
  };

  const handleAssignDocument = (item) => {
    if (item?.value) {
      dispatch(
        coreActions.requestAssignDocumentToClient({
          id: item.value,
          payload: {
            user_id: clientInfo.id,
          },
        }),
      );
    }
  };

  const selectProps = {
    programs: {
      placeholder: i18n.t('search_assign.placeholder.programTemplates'),
      options: [
        { newOne: true, label: 'assign new one' },
        ...searchOptions,
      ],
      components: {
        Option: AssignSearchOption,
      },
      archive: true,
      onChange: handleSelectChange,
    },
    workouts: {
      placeholder: i18n.t('search_assign.placeholder.workoutTemplates'),
      options: [
        { newOne: true, label: 'assign new one' },
        ...searchOptions,
      ],
      components: {
        Option: AssignSearchOption,
      },
      archive: true,
      onChange: handleSelectChange,
    },
    exercises: {
      placeholder: i18n.t('search_assign.placeholder.exercises'),
      options: searchOptions,
      onChange: handleSelectChange,
    },
    documents: {
      placeholder: i18n.t('search_assign.placeholder.documents'),
      options: searchOptions,
      onChange: handleAssignDocument,
    },
  }[tabName];

  // eslint-disable-next-line
  const clientName = `${clientInfo.user_profile?.first_name} ${clientInfo.user_profile?.last_name}`;

  return (
    <Flex
      fillSpace
      column
    >
      <Header
        id={id}
        clientInfo={clientInfo}
      />
      <Flex
        fillSpace
        margin="30px 0 0 0"
      >
        <ContentWrapper
          header={(
            <Flex
              fillSpace
              column
            >
              <Flex
                width="100%"
                align="center"
                justify="space-between"
              >
                <Flex
                  align="center"
                  spacing={10}
                >
                  <NavigationTabs
                    tabs={userTabs}
                  />
                </Flex>
                <Flex
                  spacing={10}
                >
                  {{
                    programs: (
                      <Button
                        icon="fa fa-plus"
                        appearance="success"
                        onClick={() => setNewItemModal(true)}
                      >
                        {i18n.t('add.program')}
                      </Button>
                    ),
                    workouts: (
                      <Button
                        icon="fa fa-plus"
                        appearance="success"
                        onClick={() => setNewItemModal(true)}
                      >
                        {i18n.t('add.workout')}
                      </Button>
                    ),
                  }[tabName]}
                </Flex>
              </Flex>
              {selectProps && (
                <S.Search>
                  <Select
                    {...selectProps}
                    value=""
                    styles={customStyles}
                    minMenuHeight={100}
                    maxMenuHeight={300}
                    menuPosition="fixed"
                  />
                  {selectProps.archive && (
                    <Button
                      appearance="link"
                      onClick={() => {
                        history.push({
                          pathname: `/users/${id}/${tabName}`,
                          search: `?status=${isArchived ? 'active' : 'inactive'}`,
                        });
                      }}
                    >
                      {`Show ${isArchived ? 'Active' : 'Archived'}`}
                    </Button>
                  )}
                </S.Search>
              )}
              {tabName === 'videos' && (
                <S.Search>
                  <S.Input
                    placeholder="Search for a video"
                    value={queryVideo}
                    onChange={e => setQueryVideo(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleSearchVideo()}
                  />
                  <S.SearchButton
                    onClick={handleSearchVideo}
                  >
                    <Icon size="15px" type="fas fa-search" />
                  </S.SearchButton>
                </S.Search>
              )}
            </Flex>
          )}
        >
          {{
            programs: (
              <Templates
                params={params}
                isArchived={isArchived}
                folderId={folderRoots.programId}
                clientInfo={clientInfo}
                resourceType={resourceType}
              />
            ),
            workouts: (
              <Templates
                params={params}
                isArchived={isArchived}
                folderId={folderRoots.workoutId}
                clientInfo={clientInfo}
                resourceType={resourceType}
              />
            ),
            exercises: (
              <Exercises
                folderId={folderRoots.exerciseId}
                clientInfo={clientInfo}
                resourceType={resourceType}
              />
            ),
            videos: (
              <Videos
                clientInfo={clientInfo}
                resourceType={resourceType}
              />
            ),
            calendar: (
              <CalendarTab
                clientInfo={clientInfo}
              />
            ),
            documents: (
              <Documents
                clientInfo={clientInfo}
                resourceType={resourceType}
              />
            ),
          }[tabName]}
        </ContentWrapper>

        {profileId !== clientInfo.id && (
          <RightWidget
            tabName={tabName}
            clientId={clientInfo.id}
          />
        )}
      </Flex>
      <AddEntity
        label={i18n.t(`client.create.${tabName}`, { clientName })}
        buttonLabel="Create"
        closeButton
        isOpen={newItemModal}
        onRequestClose={() => {
          setNewItemModal(false);
        }}
        onSubmit={(values, actions) => {
          dispatch(
            coreActions.requestCreatePersonalEntity({
              clientId: clientInfo.id,
              payload: {
                ...values,
                is_cooldown: false,
                is_visible: false,
                is_warmup: false,
              },
              callbacks: {
                ...actions,
                onSuccess: (res) => {
                  let redirect;
                  switch (tabName) {
                    case 'workouts':
                      redirect = `/personal_workouts/${res.id}/edit`;
                      break;
                    case 'programs':
                      redirect = `/personal_programs/${res.id}/edit/workouts`;
                      break;
                    default:
                  }
                  setNewItemModal(false);
                  history.push(redirect);
                },
              },
              resourceType,
            }),
          );
        }}
      />
    </Flex>
  );
};

export default UserDetails;
